import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Search,
  ArrowLeft,
  Loader2,
  SlidersHorizontal,
} from "lucide-react";
import { fetchAlerts } from "../../../common/alertsApi.js";
import PlayerRiskModeToggle from "../../../common/PlayerRiskModeToggle.jsx";
import {
  buildActiveAlertPlayerIdSet,
  DEFAULT_TEAM_OVERVIEW_RISK_MODE,
  hasLoadSimulationData,
  isPlayerAtRisk,
  PLAYER_AT_RISK_TRAINABILITY_THRESHOLD,
  readTeamOverviewRiskMode,
  TEAM_OVERVIEW_RISK_MODE_AT_RISK,
  TEAM_OVERVIEW_RISK_MODE_STORAGE_KEY,
} from "../../../common/playerRiskUtils.js";
import WeeklyRecapPlayerPanel from "./WeeklyRecapPlayerPanel.jsx";
import WeeklyRecapPlanningModal from "./WeeklyRecapPlanningModal.jsx";
import WeeklyRecapTargetMatchSelect from "./WeeklyRecapTargetMatchSelect.jsx";
import {
  WEEKLY_RECAP_J_DAYS,
  WEEKLY_RECAP_REFERENCE_METRICS,
  WEEKLY_RECAP_INTERNAL_METRICS,
  WEEKLY_RECAP_LOAD_METRICS,
  filterWeeklyRecapLoadMetrics,
  offsetToJDayLabel,
  buildJDaysFromRange,
  createDefaultPercentageMatrix,
  normalizeMetricLabelOverrides,
  normalizeDayLabelOverrides,
  normalizeMicrocycleConfig,
  normalizePercentageMatrix,
  getWeeklySimilarityRingColor,
  getWeeklySimilarityTone,
  resolveWeeklyRecapVisibleDays,
} from "./weeklyRecapConfig";
import {
  fetchWeeklyRecapGlobalPlan,
  fetchPlayerWeeklyRecapBundle,
  applyGlobalPlanToBundle,
  updateWeeklyRecapGlobalPlan,
  updatePlayerWeeklyRecapReferences,
  rebuildBundleWithNewReferences,
  computeWeeklySimilarityForDays,
  buildWeeklyRecapBundleCacheKey,
} from "./weeklyRecapApi";
import { isMaquetteClub } from "./weeklyRecapMaquetteMock";
import { normalizePlayerPosition } from "../../../common/PlayerPositions";

const ALERTS_REFRESH_INTERVAL_MS = 60 * 1000;

function getPlayerDisplayName(player) {
  return `${player?.first_name || ""} ${player?.last_name || ""}`.trim() || "Player";
}

function toPlayerAvatarSrc(player) {
  if (!player?.profile_picture) return null;
  return `data:image/jpeg;base64,${player.profile_picture}`;
}

function PositionFilter({ value, onChange, isDarkMode, t }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`w-full rounded-xl border px-3 py-2 text-sm ${
        isDarkMode
          ? "border-gray-600 bg-gray-800 text-gray-100"
          : "border-gray-300 bg-white text-gray-900"
      }`}
    >
      <option value="All">{t("positions.all", { defaultValue: "All positions" })}</option>
      <option value="Defense">{t("positions.defense", { defaultValue: "Defense" })}</option>
      <option value="Midfield">{t("positions.midfield", { defaultValue: "Midfield" })}</option>
      <option value="Forward">{t("positions.forward", { defaultValue: "Forward" })}</option>
      <option value="Lateral">{t("positions.lateral", { defaultValue: "Lateral" })}</option>
    </select>
  );
}

function AvatarWithProgress({ player, value, isDarkMode, size = "md" }) {
  const initials = `${player?.first_name?.[0] || "P"}${player?.last_name?.[0] || ""}`.toUpperCase();
  const avatarSrc = toPlayerAvatarSrc(player);
  const hasValue = typeof value === "number" && Number.isFinite(value);
  const safe = Math.max(0, Math.min(100, hasValue ? value : 0));
  const ringColor = getWeeklySimilarityRingColor(hasValue ? value : null);
  const isLarge = size === "lg";
  const box = isLarge ? 64 : 44;
  const inner = isLarge ? 48 : 32;
  const radius = isLarge ? 28 : 18;
  const strokeWidth = isLarge ? 3.5 : 2.5;
  const center = box / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (safe / 100) * circumference;

  return (
    <div
      className="relative flex items-center justify-center shrink-0"
      style={{ width: box, height: box }}
    >
      <svg
        className="absolute inset-0 transform -rotate-90"
        width={box}
        height={box}
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          className={isDarkMode ? "stroke-gray-800" : "stroke-gray-100"}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {safe > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={ringColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            fill="transparent"
            style={{ transition: "stroke-dashoffset 0.6s ease-out, stroke 0.3s ease" }}
          />
        )}
      </svg>
      <div
        className="rounded-full overflow-hidden select-none flex items-center justify-center relative bg-gray-200 dark:bg-gray-800"
        style={{ width: inner, height: inner }}
      >
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt={getPlayerDisplayName(player)}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className={`${isLarge ? "text-sm" : "text-[10px]"} font-bold text-gray-600 dark:text-gray-300`}>
            {initials}
          </span>
        )}
      </div>
    </div>
  );
}

function getRosterStatus(value, t) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return {
      id: "na",
      label: t("common.na", { defaultValue: "N/A" }),
    };
  }
  if (value >= 80) {
    return {
      id: "aligned",
      label: t("weekly_recap.roster.status_aligned", { defaultValue: "On track" }),
    };
  }
  if (value >= 60) {
    return {
      id: "watch",
      label: t("weekly_recap.roster.status_watch", { defaultValue: "Watch" }),
    };
  }
  return {
    id: "gap",
    label: t("weekly_recap.roster.status_gap", { defaultValue: "Gap" }),
  };
}

function PlayerRecapCard({
  player,
  similarity,
  bundle,
  isDarkMode,
  isAtRisk,
  t,
  onSelect,
}) {
  const loading = Boolean(bundle?.loading);
  const error = bundle?.error;
  const hasManual = Object.keys(bundle?.referenceOverrides || {}).length > 0
    || Object.values(bundle?.referenceMetricSources || {}).some((source) => source?.kind === "manual");
  const missingData = bundle?.status === "no_completed_target_match"
    || bundle?.availableTargetMatches?.some((item) => item?.is_selected && item?.player_has_match_data === false);
  const status = getRosterStatus(similarity, t);
  const borderColor = error
    ? "border-rose-500"
    : loading
      ? "border-gray-400"
      : similarity == null
        ? "border-gray-400"
        : similarity >= 80
          ? "border-emerald-500"
          : similarity >= 60
            ? "border-amber-400"
            : "border-rose-500";

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-2xl border-l-4 ${borderColor} p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        isDarkMode
          ? "bg-gray-800/80 hover:bg-gray-800 ring-1 ring-white/10"
          : "bg-white hover:bg-gray-50 ring-1 ring-black/5"
      }`}
    >
      <div className="flex items-start gap-3">
        <AvatarWithProgress
          player={player}
          value={similarity}
          isDarkMode={isDarkMode}
          size="lg"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold">
            {getPlayerDisplayName(player)}
          </div>
          <div className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] ${
            isDarkMode ? "bg-gray-900/70 text-gray-300" : "bg-gray-100 text-gray-600"
          }`}>
            {player?.position || t("common.na", { defaultValue: "N/A" })}
          </div>
        </div>
        <div className="text-right shrink-0">
          {loading ? (
            <Loader2 className="ml-auto h-5 w-5 animate-spin text-cyan-500" />
          ) : (
            <div className="text-2xl font-extrabold tabular-nums leading-none">
              {typeof similarity === "number" && Number.isFinite(similarity)
                ? `${similarity.toFixed(0)}%`
                : t("common.na", { defaultValue: "N/A" })}
            </div>
          )}
          <div className={`mt-1 text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {t("weekly_recap.sidebar.week", { defaultValue: "Week" })}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${getWeeklySimilarityTone(similarity, isDarkMode)}`}>
          {status.label}
        </span>
        {isAtRisk ? (
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            isDarkMode ? "bg-rose-900/40 text-rose-200" : "bg-rose-100 text-rose-700"
          }`}>
            {t("weekly_recap.roster.status_risk", { defaultValue: "At risk" })}
          </span>
        ) : null}
        {hasManual ? (
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            isDarkMode ? "bg-amber-900/40 text-amber-200" : "bg-amber-100 text-amber-800"
          }`}>
            {t("weekly_recap.roster.status_manual", { defaultValue: "Manual refs" })}
          </span>
        ) : null}
        {missingData ? (
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            isDarkMode ? "bg-gray-800 text-gray-300" : "bg-gray-100 text-gray-600"
          }`}>
            {t("weekly_recap.roster.status_missing", { defaultValue: "Missing data" })}
          </span>
        ) : null}
        {error ? (
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            isDarkMode ? "bg-rose-900/40 text-rose-200" : "bg-rose-100 text-rose-700"
          }`}>
            {t("common.error", { defaultValue: "Error" })}
          </span>
        ) : null}
      </div>
    </button>
  );
}

const CHART_MODE_ABSOLUTE = "absolute";
const CHART_MODE_PERCENT = "percent";

function getMetricKeys(metrics) {
  return (metrics || []).map((metric) => metric.key).filter(Boolean);
}

function normalizeMetricKeys(keys, metrics) {
  const allowed = new Set(getMetricKeys(metrics));
  const filtered = (keys || []).filter((key) => allowed.has(key));

  if (filtered.length > 0) return filtered;

  const fallback = metrics?.[0]?.key;
  return fallback ? [fallback] : [];
}

function createDefaultDisplayConfig() {
  return {
    chartDisplayMode: CHART_MODE_PERCENT,
    externalMetricKeys: getMetricKeys(WEEKLY_RECAP_LOAD_METRICS),
    internalMetricKeys: getMetricKeys(WEEKLY_RECAP_INTERNAL_METRICS),
    metricLabelOverrides: {},
    dayLabelOverrides: {},
  };
}

function normalizeDisplayConfig(config) {
  const chartDisplayMode =
    config?.chartDisplayMode === CHART_MODE_PERCENT
      ? CHART_MODE_PERCENT
      : CHART_MODE_ABSOLUTE;

  return {
    chartDisplayMode,
    externalMetricKeys: normalizeMetricKeys(
      config?.externalMetricKeys,
      WEEKLY_RECAP_LOAD_METRICS,
    ),
    internalMetricKeys: normalizeMetricKeys(
      config?.internalMetricKeys,
      WEEKLY_RECAP_INTERNAL_METRICS,
    ),
    metricLabelOverrides: normalizeMetricLabelOverrides(
      config?.metricLabelOverrides || config?.metric_label_overrides,
      [...WEEKLY_RECAP_REFERENCE_METRICS, ...WEEKLY_RECAP_INTERNAL_METRICS],
    ),
    dayLabelOverrides: normalizeDayLabelOverrides(
      config?.dayLabelOverrides || config?.day_label_overrides,
    ),
  };
}

function labelToOffset(label) {
  if (typeof label !== "string") return null;

  const txt = label.trim().toUpperCase();
  for (const prefix of ["MD", "J"]) {
    if (txt === prefix) return 0;
    if (!txt.startsWith(prefix)) continue;

    const suffixRaw = txt.slice(prefix.length);
    const suffix = suffixRaw.startsWith("+") ? suffixRaw.slice(1) : suffixRaw;
    const n = Number(suffix);
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

function normalizeSimilarityDayLabel(label, jDays, fallbackLabel) {
  const labels = Array.isArray(jDays) ? jDays : [];

  if (typeof label === "string" && labels.includes(label)) {
    return label;
  }

  const offset = labelToOffset(label);
  if (offset !== null) {
    const mdLabel = offsetToJDayLabel(offset);
    if (labels.includes(mdLabel)) {
      return mdLabel;
    }
  }

  if (typeof fallbackLabel === "string" && labels.includes(fallbackLabel)) {
    return fallbackLabel;
  }

  return labels[0] || fallbackLabel || "MD+1";
}

function toNullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function computeWeeklySimilarityFromDays({
  metrics,
  jDays,
  plannedValues,
  actualValues,
  matchDayLabel,
}) {
  const { weeklyPercentDays } = resolveWeeklyRecapVisibleDays({
    jDays,
    actualValues,
    metrics,
    matchDayLabel,
  });

  return computeWeeklySimilarityForDays({
    plannedMatrix: plannedValues,
    actualMatrix: actualValues,
    metrics,
    jDays: weeklyPercentDays,
  });
}

export default function WeeklyRecapTab({
  players,
  season,
  clubId,
  isDarkMode,
  isDev = false,
  metrics = WEEKLY_RECAP_REFERENCE_METRICS,
  defaultJDays = WEEKLY_RECAP_J_DAYS,
}) {
  const { t } = useTranslation();
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [riskMode, setRiskMode] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_TEAM_OVERVIEW_RISK_MODE;
    return readTeamOverviewRiskMode(window.sessionStorage);
  });
  const [activeAlertPlayerIds, setActiveAlertPlayerIds] = useState(() => new Set());
  const [bundleByPlayerId, setBundleByPlayerId] = useState({});
  const [planningModalOpen, setPlanningModalOpen] = useState(false);
  const [savingGlobalPlan, setSavingGlobalPlan] = useState(false);
  const [globalStatusMessage, setGlobalStatusMessage] = useState(null);
  const [updatingTargetMatch, setUpdatingTargetMatch] = useState(false);
  const [savingPlayerReferences, setSavingPlayerReferences] = useState(false);
  const [globalTargetMatchDate, setGlobalTargetMatchDate] = useState(null);
  const [globalAvailableTargetMatches, setGlobalAvailableTargetMatches] = useState([]);
  const inFlightBundleKeysRef = useRef(new Set());
  const globalPlanPercentagesRef = useRef(null);
  const bundleByPlayerIdRef = useRef({});

  useEffect(() => {
    try {
      window.sessionStorage.setItem(TEAM_OVERVIEW_RISK_MODE_STORAGE_KEY, riskMode);
    } catch {
      // Session storage can be unavailable in embedded browser contexts.
    }
  }, [riskMode]);

  useEffect(() => {
    let cancelled = false;

    const refreshAlerts = async () => {
      try {
        const { alerts } = await fetchAlerts({ status: "all", limit: 200, offset: 0 });
        if (!cancelled) {
          setActiveAlertPlayerIds(buildActiveAlertPlayerIdSet(alerts));
        }
      } catch {
        if (!cancelled) {
          setActiveAlertPlayerIds(new Set());
        }
      }
    };

    refreshAlerts();
    const interval = setInterval(refreshAlerts, ALERTS_REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);
  
  // Custom interactive switches
  const [isInternalMode, setIsInternalMode] = useState(false);
  const [calculateSimilarityFromDay, setCalculateSimilarityFromDay] = useState("MD+1");
  const [microcycleConfig, setMicrocycleConfig] = useState(() =>
    normalizeMicrocycleConfig(null),
  );
  const [effectiveJDays, setEffectiveJDays] = useState(defaultJDays);
  const [configReady, setConfigReady] = useState(false);

  const [displayConfig, setDisplayConfig] = useState(() =>
    createDefaultDisplayConfig(),
  );

  const allMetrics = useMemo(() => {
    return [...WEEKLY_RECAP_REFERENCE_METRICS, ...WEEKLY_RECAP_INTERNAL_METRICS];
  }, []);

  const [globalPlanPercentages, setGlobalPlanPercentages] = useState(() =>
    createDefaultPercentageMatrix(allMetrics, defaultJDays),
  );

  useEffect(() => {
    setGlobalPlanPercentages((prev) => normalizePercentageMatrix(prev, allMetrics, defaultJDays));
  }, [allMetrics, defaultJDays]);

  useEffect(() => {
    globalPlanPercentagesRef.current = globalPlanPercentages;
  }, [globalPlanPercentages]);

  useEffect(() => {
    bundleByPlayerIdRef.current = bundleByPlayerId;
  }, [bundleByPlayerId]);

  useEffect(() => {
    let cancelled = false;

    const loadGlobalConfig = async () => {
      setConfigReady(false);
      inFlightBundleKeysRef.current.clear();

      const payload = await fetchWeeklyRecapGlobalPlan({
        season,
        metrics: allMetrics,
        fallbackJDays: defaultJDays,
        clubId,
      });

      if (cancelled) return;

      const nextJDays = Array.isArray(payload?.jDays) && payload.jDays.length > 0
        ? payload.jDays
        : defaultJDays;

      const nextPlan = normalizePercentageMatrix(
        payload?.planPercentages,
        allMetrics,
        nextJDays,
      );

      setEffectiveJDays(nextJDays);
      setGlobalPlanPercentages(nextPlan);
      setGlobalTargetMatchDate(payload?.targetMatchDate || null);
      setGlobalAvailableTargetMatches(
        Array.isArray(payload?.availableTargetMatches) ? payload.availableTargetMatches : [],
      );
      setBundleByPlayerId({});

      if (payload?.displayConfig) {
        setDisplayConfig(normalizeDisplayConfig(payload.displayConfig));
      }

      const nextMicrocycle = normalizeMicrocycleConfig(payload?.microcycle);
      setMicrocycleConfig(nextMicrocycle);

      const similarityFrom =
        payload?.calculateSimilarityFromDay ||
        nextMicrocycle.calculate_similarity_from ||
        "MD+1";

      setCalculateSimilarityFromDay(
        normalizeSimilarityDayLabel(
          similarityFrom,
          nextJDays,
          nextMicrocycle.calculate_similarity_from,
        ),
      );

      setConfigReady(true);
    };

    loadGlobalConfig().catch(() => {
      if (cancelled) return;
      setConfigReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [season, allMetrics, defaultJDays, clubId]);

  const playersWithSimulation = useMemo(() => {
    if (isMaquetteClub(clubId)) {
      return (players || []).filter(
        (player) => normalizePlayerPosition(player?.position) !== "Goalkeeper",
      );
    }
    return (players || []).filter(hasLoadSimulationData);
  }, [players, clubId]);

  const visiblePlayers = useMemo(() => {
    if (riskMode !== TEAM_OVERVIEW_RISK_MODE_AT_RISK) return playersWithSimulation;

    return playersWithSimulation.filter((player) =>
      isPlayerAtRisk(player, {
        activeAlertPlayerIds,
        trainabilityThreshold: PLAYER_AT_RISK_TRAINABILITY_THRESHOLD,
      })
    );
  }, [playersWithSimulation, riskMode, activeAlertPlayerIds]);

  const filteredPlayers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    return visiblePlayers.filter((player) => {
      if (selectedPosition !== "All" && player?.position !== selectedPosition) return false;
      if (!q) return true;
      const name = getPlayerDisplayName(player).toLowerCase();
      return name.includes(q);
    });
  }, [visiblePlayers, searchTerm, selectedPosition]);

  const selectedExternalMetrics = useMemo(() => {
    const keys = new Set(displayConfig.externalMetricKeys);
    return filterWeeklyRecapLoadMetrics(
      WEEKLY_RECAP_LOAD_METRICS.filter((metric) => keys.has(metric.key)),
    );
  }, [displayConfig.externalMetricKeys]);

  const selectedInternalMetrics = useMemo(() => {
    const keys = new Set(displayConfig.internalMetricKeys);
    return WEEKLY_RECAP_INTERNAL_METRICS.filter((metric) => keys.has(metric.key));
  }, [displayConfig.internalMetricKeys]);

  const visibleMetrics = isInternalMode ? selectedInternalMetrics : selectedExternalMetrics;

  useEffect(() => {
    if (!selectedPlayerId) return;
    const stillVisible = filteredPlayers.some((player) => player?.id === selectedPlayerId);
    if (!stillVisible) {
      setSelectedPlayerId(null);
    }
  }, [filteredPlayers, selectedPlayerId]);

  const loadPlayerBundle = useCallback(
    async (player, { force = false, targetMatchDateOverride } = {}) => {
      if (!player?.id || !configReady) return;

      const resolvedTargetMatchDate = targetMatchDateOverride !== undefined
        ? targetMatchDateOverride
        : globalTargetMatchDate;

      const cacheKey = buildWeeklyRecapBundleCacheKey(
        player.id,
        season,
        resolvedTargetMatchDate,
      );

      const current = bundleByPlayerIdRef.current?.[player.id];
      if (!force && current?.cacheKey === cacheKey && current?.fetchedAt && !current?.error) {
        return;
      }

      if (inFlightBundleKeysRef.current.has(cacheKey)) {
        return;
      }

      inFlightBundleKeysRef.current.add(cacheKey);

      setBundleByPlayerId((prev) => ({
        ...prev,
        [player.id]: {
          ...(prev?.[player.id] || {}),
          loading: true,
          error: null,
          cacheKey,
        },
      }));

      try {
        const bundle = await fetchPlayerWeeklyRecapBundle({
          player,
          season,
          metrics: allMetrics,
          jDays: effectiveJDays,
          targetMatchDate: resolvedTargetMatchDate || null,
          clubId,
          globalPlanPercentages: globalPlanPercentagesRef.current || globalPlanPercentages,
        });

        const mergedBundle = applyGlobalPlanToBundle(
          bundle,
          globalPlanPercentagesRef.current || globalPlanPercentages,
          allMetrics,
        );

        setBundleByPlayerId((prev) => ({
          ...prev,
          [player.id]: {
            ...mergedBundle,
            loading: false,
            error: null,
            cacheKey,
          },
        }));
      } catch (error) {
        setBundleByPlayerId((prev) => ({
          ...prev,
          [player.id]: {
            ...(prev?.[player.id] || {}),
            loading: false,
            error: error?.message || t("weekly_recap.errors.load", { defaultValue: "Unable to load weekly recap" }),
            cacheKey,
          },
        }));
      } finally {
        inFlightBundleKeysRef.current.delete(cacheKey);
      }
    },
    [
      configReady,
      season,
      allMetrics,
      effectiveJDays,
      globalTargetMatchDate,
      globalPlanPercentages,
      clubId,
      t,
    ],
  );

  useEffect(() => {
    if (!configReady || !visiblePlayers.length) return;

    visiblePlayers.forEach((player) => {
      loadPlayerBundle(player);
    });
  }, [configReady, visiblePlayers, season, globalTargetMatchDate, effectiveJDays, loadPlayerBundle]);

  useEffect(() => {
    if (!selectedPlayerId) return;
    const player = visiblePlayers.find((entry) => entry?.id === selectedPlayerId);
    if (!player) return;
    loadPlayerBundle(player, { force: false });
  }, [selectedPlayerId, visiblePlayers, loadPlayerBundle]);

  const selectedPlayer = useMemo(
    () => visiblePlayers.find((player) => player?.id === selectedPlayerId) || null,
    [visiblePlayers, selectedPlayerId],
  );

  const selectedBundle = selectedPlayerId ? bundleByPlayerId?.[selectedPlayerId] : null;

  const effectiveTargetMatches = useMemo(() => {
    if (Array.isArray(globalAvailableTargetMatches) && globalAvailableTargetMatches.length > 0) {
      return globalAvailableTargetMatches;
    }

    if (Array.isArray(selectedBundle?.availableTargetMatches)) {
      return selectedBundle.availableTargetMatches;
    }

    return [];
  }, [globalAvailableTargetMatches, selectedBundle]);

  const effectiveTargetMatchDate = globalTargetMatchDate || selectedBundle?.targetMatchDate || null;

  const effectiveTargetMatchOpponent = useMemo(() => {
    const found = effectiveTargetMatches.find((item) => item?.date === effectiveTargetMatchDate);
    return found?.opponent || selectedBundle?.targetMatchOpponent || null;
  }, [effectiveTargetMatches, effectiveTargetMatchDate, selectedBundle]);

  const applyGlobalMatrixToLoadedBundles = useCallback((nextMatrix, sourceDetailsText) => {
    setBundleByPlayerId((prev) => {
      const next = {};

      Object.keys(prev || {}).forEach((playerId) => {
        const current = prev[playerId] || {};
        const rebuilt = applyGlobalPlanToBundle(current, nextMatrix, allMetrics);
        next[playerId] = {
          ...rebuilt,
          loading: false,
          error: null,
          sourceDetails: sourceDetailsText,
        };
      });

      return next;
    });
  }, [allMetrics]);

  const saveGlobalConfiguration = async ({
    planPercentages,
    displayConfig: nextDisplayConfig,
    microcycle: nextMicrocycle,
    calculateSimilarityFromDay: nextCalculateSimilarityFromDay,
    jDays,
  }) => {
    const normalizedMicrocycle = normalizeMicrocycleConfig(nextMicrocycle, microcycleConfig);
    const targetJDays = Array.isArray(jDays) && jDays.length > 0
      ? jDays
      : buildJDaysFromRange(
        normalizedMicrocycle.start_offset,
        normalizedMicrocycle.end_offset,
      );

    const normalized = normalizePercentageMatrix(
      planPercentages,
      allMetrics,
      targetJDays,
    );

    const normalizedDisplayConfig = normalizeDisplayConfig(nextDisplayConfig);
    const nextSimilarityFrom =
      nextCalculateSimilarityFromDay ||
      normalizedMicrocycle.calculate_similarity_from ||
      calculateSimilarityFromDay;
    const normalizedSimilarityFrom = normalizeSimilarityDayLabel(
      nextSimilarityFrom,
      targetJDays,
      normalizedMicrocycle.calculate_similarity_from,
    );

    setGlobalPlanPercentages(normalized);
    setDisplayConfig(normalizedDisplayConfig);
    setMicrocycleConfig(normalizedMicrocycle);
    setCalculateSimilarityFromDay(normalizedSimilarityFrom);
    setEffectiveJDays(targetJDays);

    applyGlobalMatrixToLoadedBundles(
      normalized,
      t("weekly_recap.messages.applied_local", {
        defaultValue: "Global configuration applied to loaded players",
      }),
    );

    setPlanningModalOpen(false);

    try {
      setSavingGlobalPlan(true);
      setGlobalStatusMessage(null);

      const persisted = await updateWeeklyRecapGlobalPlan({
        season,
        planPercentages: normalized,
        displayConfig: normalizedDisplayConfig,
        microcycle: normalizedMicrocycle,
        calculateSimilarityFromDay: normalizedSimilarityFrom,
      });

      const persistedJDays = Array.isArray(persisted?.j_days) && persisted.j_days.length > 0
        ? persisted.j_days
        : targetJDays;
      setEffectiveJDays(persistedJDays);

      if (persisted?.microcycle) {
        const persistedMicrocycle = normalizeMicrocycleConfig(persisted.microcycle, normalizedMicrocycle);
        setMicrocycleConfig(persistedMicrocycle);

        const persistedSimilarityFrom =
          persisted?.microcycle?.effective_calculate_similarity_from ||
          persisted?.microcycle?.calculate_similarity_from ||
          persistedMicrocycle.calculate_similarity_from;

        setCalculateSimilarityFromDay(
          normalizeSimilarityDayLabel(
            persistedSimilarityFrom,
            persistedJDays,
            persistedMicrocycle.calculate_similarity_from,
          ),
        );
      }

      const successText = t("weekly_recap.messages.saved_global", {
        defaultValue: "Global configuration saved",
      });

      setGlobalStatusMessage({ tone: "success", text: successText });
      applyGlobalMatrixToLoadedBundles(normalized, successText);
    } catch (error) {
      const fallbackText =
        error?.message ||
        t("weekly_recap.messages.save_failed", {
          defaultValue: "Global save endpoint unavailable",
        });

      setGlobalStatusMessage({ tone: "warning", text: fallbackText });
      applyGlobalMatrixToLoadedBundles(normalized, fallbackText);
    } finally {
      setSavingGlobalPlan(false);
    }
  };

  const handleTargetMatchChange = useCallback(async (nextTargetMatchDate) => {
    const nextGlobalTarget = nextTargetMatchDate || null;
    setGlobalTargetMatchDate(nextGlobalTarget);
    inFlightBundleKeysRef.current.clear();

    const reloadBundles = async () => {
      setBundleByPlayerId((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((playerId) => {
          const entry = next[playerId];
          if (!entry || entry.cacheKey === buildWeeklyRecapBundleCacheKey(playerId, season, nextGlobalTarget)) {
            return;
          }
          next[playerId] = {
            ...entry,
            loading: true,
            error: null,
          };
        });
        return next;
      });

      if (visiblePlayers.length) {
        await Promise.all(
          visiblePlayers.map((player) =>
            loadPlayerBundle(player, { force: true, targetMatchDateOverride: nextGlobalTarget }),
          ),
        );
      }
    };

    setUpdatingTargetMatch(true);

    if (isMaquetteClub(clubId)) {
      setGlobalAvailableTargetMatches((prev) =>
        (prev || []).map((item) => ({
          ...item,
          is_selected: item.date === nextGlobalTarget,
        })),
      );

      try {
        await reloadBundles();
      } finally {
        setUpdatingTargetMatch(false);
      }
      return;
    }

    try {
      const persisted = await updateWeeklyRecapGlobalPlan({
        season,
        targetMatchDate: nextGlobalTarget,
      });

      if (Array.isArray(persisted?.available_target_matches)) {
        setGlobalAvailableTargetMatches(persisted.available_target_matches);
      }

      await reloadBundles();
    } catch (error) {
      setGlobalStatusMessage({
        tone: "warning",
        text: error?.message || t("weekly_recap.messages.save_failed", {
          defaultValue: "Global save endpoint unavailable",
        }),
      });
    } finally {
      setUpdatingTargetMatch(false);
    }
  }, [season, visiblePlayers, loadPlayerBundle, clubId, t]);

  const handleSavePlayerReferences = useCallback(async ({
    referenceMetrics,
    resetMetrics,
    resetAll = false,
  } = {}) => {
    if (!selectedPlayerId) return;

    const applyLocalOverrides = (nextOverrides) => {
      setBundleByPlayerId((prev) => {
        const current = prev?.[selectedPlayerId];
        if (!current) return prev;
        return {
          ...prev,
          [selectedPlayerId]: {
            ...rebuildBundleWithNewReferences(current, allMetrics, nextOverrides),
            loading: false,
            error: null,
            cacheKey: current.cacheKey,
          },
        };
      });
    };

    const buildLocalOverrides = () => {
      const current = bundleByPlayerIdRef.current?.[selectedPlayerId] || {};
      let next = { ...(current.referenceOverrides || {}) };
      if (resetAll) {
        next = {};
      } else {
        (resetMetrics || []).forEach((key) => {
          delete next[key];
        });
        Object.entries(referenceMetrics || {}).forEach(([key, value]) => {
          if (value === null || value === "") {
            delete next[key];
            return;
          }
          const numeric = Number(value);
          if (Number.isFinite(numeric)) next[key] = numeric;
        });
      }
      return next;
    };

    setSavingPlayerReferences(true);
    setGlobalStatusMessage(null);
    try {
      if (isMaquetteClub(clubId)) {
        applyLocalOverrides(buildLocalOverrides());
      } else {
        const persisted = await updatePlayerWeeklyRecapReferences({
          playerId: selectedPlayerId,
          season,
          referenceMetrics,
          resetMetrics,
          resetAll,
        });
        applyLocalOverrides(persisted?.reference_overrides || {});
      }

      setGlobalStatusMessage({
        tone: "success",
        text: t("weekly_recap.messages.saved_references", {
          defaultValue: "Player references saved",
        }),
      });
    } catch (error) {
      setGlobalStatusMessage({
        tone: "warning",
        text: error?.message || t("weekly_recap.messages.save_references_failed", {
          defaultValue: "Unable to save player references",
        }),
      });
      throw error;
    } finally {
      setSavingPlayerReferences(false);
    }
  }, [selectedPlayerId, season, allMetrics, clubId, t]);

  const getPlayerCustomSimilarity = useCallback((playerId) => {
    const bundle = bundleByPlayerId?.[playerId];
    if (!bundle) return null;

    if (Number.isFinite(bundle?.externalWeeklySimilarity)) {
      return bundle.externalWeeklySimilarity;
    }

    if (Number.isFinite(bundle?.weeklySimilarity)) {
      return bundle.weeklySimilarity;
    }

    return computeWeeklySimilarityFromDays({
      metrics: selectedExternalMetrics,
      jDays: bundle?.jDays || effectiveJDays || defaultJDays,
      plannedValues: bundle?.plannedValues || {},
      actualValues: bundle?.actualValues || {},
      matchDayLabel: bundle?.nextMatchDay || "MD",
    });
  }, [
    bundleByPlayerId,
    selectedExternalMetrics,
    effectiveJDays,
    defaultJDays,
  ]);

  const selectedWeeklySimilarity = selectedPlayerId
    ? getPlayerCustomSimilarity(selectedPlayerId)
    : null;

  const handleSelectPlayer = (playerId) => {
    setSelectedPlayerId(playerId);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const toolbar = (
    <div className={`rounded-2xl border p-4 ${
      isDarkMode ? "border-gray-800 bg-gray-900/50" : "border-gray-200 bg-white"
    }`}>
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0">
          {selectedPlayer ? (
            <button
              type="button"
              onClick={() => setSelectedPlayerId(null)}
              className={`mb-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-semibold ${
                isDarkMode ? "text-gray-200 hover:bg-gray-800" : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <ArrowLeft className="h-4 w-4" />
              {t("weekly_recap.roster.back", { defaultValue: "Back to players" })}
            </button>
          ) : null}
          <h1 className="text-xl font-extrabold tracking-tight">
            {t("weekly_recap.title", { defaultValue: "Weekly recap report" })}
          </h1>
          <p className={`mt-1 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {selectedPlayer
              ? t("weekly_recap.roster.detail_subtitle", {
                defaultValue: "Player detail for the selected target match.",
              })
              : t("weekly_recap.subtitle", {
                defaultValue: "Select a player to view planning vs actual loads.",
              })}
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <WeeklyRecapTargetMatchSelect
            value={effectiveTargetMatchDate}
            matches={effectiveTargetMatches}
            opponent={effectiveTargetMatchOpponent}
            loading={updatingTargetMatch}
            isDarkMode={isDarkMode}
            onChange={handleTargetMatchChange}
          />
          <button
            type="button"
            onClick={() => setPlanningModalOpen(true)}
            disabled={savingGlobalPlan}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-600/90 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-cyan-600 hover:shadow-lg disabled:opacity-50"
          >
            {savingGlobalPlan ? <Loader2 className="h-4 w-4 animate-spin" /> : <SlidersHorizontal className="h-4 w-4" />}
            {t("weekly_recap.actions.customize_configuration", {
              defaultValue: "Global configuration",
            })}
          </button>
        </div>
      </div>

      {updatingTargetMatch ? (
        <div className={`mt-3 flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${
          isDarkMode ? "bg-cyan-950/40 text-cyan-200" : "bg-cyan-50 text-cyan-800"
        }`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="font-medium">
            {t("weekly_recap.loading_match", { defaultValue: "Loading match data..." })}
          </span>
          <span className="opacity-80">
            {t("weekly_recap.loading_match_hint", {
              defaultValue: "Updating weekly loads for the selected match.",
            })}
          </span>
        </div>
      ) : null}

      {!selectedPlayer ? (
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <PositionFilter
            value={selectedPosition}
            onChange={setSelectedPosition}
            isDarkMode={isDarkMode}
            t={t}
          />
          <PlayerRiskModeToggle
            isDarkMode={isDarkMode}
            riskMode={riskMode}
            onChange={setRiskMode}
            className="w-full justify-between"
          />
          <label
            className={`inline-flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-sm sm:col-span-2 ${
              isDarkMode
                ? "border-gray-800 bg-gray-900/60 text-gray-200"
                : "border-gray-200 bg-white text-gray-700"
            }`}
          >
            <Search className="h-4 w-4" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t("common.search", { defaultValue: "Search" })}
              className={`w-full bg-transparent outline-none ${isDarkMode ? "placeholder:text-gray-500" : "placeholder:text-gray-400"}`}
            />
          </label>
        </div>
      ) : null}
    </div>
  );

  const renderPlayerGrid = () => {
    if (!configReady) {
      return (
        <div className={`rounded-2xl ring-1 px-4 py-10 text-center text-sm ${isDarkMode ? "bg-gray-800/60 ring-white/10 text-gray-400" : "bg-white ring-black/10 text-gray-500"}`}>
          {t("common.loading", { defaultValue: "Loading..." })}
        </div>
      );
    }

    if (!filteredPlayers.length) {
      return (
        <div className={`rounded-2xl ring-1 px-4 py-10 text-center text-sm ${isDarkMode ? "bg-gray-800/60 ring-white/10 text-gray-400" : "bg-white ring-black/10 text-gray-500"}`}>
          {t("weekly_recap.sidebar.empty", { defaultValue: "No players found" })}
        </div>
      );
    }

    return (
      <div className="relative">
        <div className={`mb-3 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          {t("weekly_recap.sidebar.count", {
            defaultValue: "{{count}} players",
            count: filteredPlayers.length,
          })}
        </div>
        <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 ${updatingTargetMatch ? "pointer-events-none opacity-70" : ""}`}>
          {filteredPlayers.map((player) => {
            const playerId = player?.id;
            return (
              <PlayerRecapCard
                key={playerId}
                player={player}
                similarity={getPlayerCustomSimilarity(playerId)}
                bundle={bundleByPlayerId?.[playerId]}
                isDarkMode={isDarkMode}
                isAtRisk={isPlayerAtRisk(player, {
                  activeAlertPlayerIds,
                  trainabilityThreshold: PLAYER_AT_RISK_TRAINABILITY_THRESHOLD,
                })}
                t={t}
                onSelect={() => handleSelectPlayer(playerId)}
              />
            );
          })}
        </div>
        {updatingTargetMatch ? (
          <div className="absolute inset-0 z-10 flex items-start justify-center pt-16">
            <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 shadow-lg ${
              isDarkMode ? "bg-gray-950/90 text-gray-100" : "bg-white/95 text-gray-900"
            }`}>
              <Loader2 className="h-5 w-5 animate-spin text-cyan-500" />
              <div>
                <div className="text-sm font-semibold">
                  {t("weekly_recap.loading_match", { defaultValue: "Loading match data..." })}
                </div>
                <div className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  {t("weekly_recap.loading_match_hint", {
                    defaultValue: "Updating weekly loads for the selected match.",
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderPlayerDetail = () => {
    if (!selectedBundle || (selectedBundle.loading && !selectedBundle.fetchedAt)) {
      return (
        <div className={`rounded-2xl ring-1 px-4 py-10 text-center text-sm ${isDarkMode ? "bg-gray-800/60 ring-white/10 text-gray-400" : "bg-white ring-black/10 text-gray-500"}`}>
          <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin text-cyan-500" />
          {t("weekly_recap.loading_player", { defaultValue: "Loading player weekly recap..." })}
        </div>
      );
    }

    if (selectedBundle?.error) {
      return (
        <div className={`rounded-2xl ring-1 px-4 py-10 text-center text-sm ${isDarkMode ? "bg-gray-800/60 ring-white/10 text-rose-300" : "bg-white ring-black/10 text-rose-600"}`}>
          {selectedBundle.error}
        </div>
      );
    }

    return (
      <div className="relative">
        <WeeklyRecapPlayerPanel
          player={selectedPlayer}
          metrics={visibleMetrics}
          similarityMetrics={selectedExternalMetrics}
          weeklySimilarityValue={selectedWeeklySimilarity}
          bundle={selectedBundle}
          isDarkMode={isDarkMode}
          isDev={isDev}
          onOpenPlanning={() => setPlanningModalOpen(true)}
          planningLoading={savingGlobalPlan}
          globalStatusMessage={globalStatusMessage}
          isInternalMode={isInternalMode}
          setIsInternalMode={setIsInternalMode}
          calculateSimilarityFromDay={calculateSimilarityFromDay}
          chartDisplayMode={displayConfig.chartDisplayMode}
          metricLabelOverrides={displayConfig.metricLabelOverrides}
          dayLabelOverrides={displayConfig.dayLabelOverrides}
          setChartDisplayMode={(mode) =>
            setDisplayConfig((prev) =>
              normalizeDisplayConfig({
                ...prev,
                chartDisplayMode: mode,
              }),
            )
          }
          onTargetMatchChange={handleTargetMatchChange}
          targetMatchLoading={updatingTargetMatch}
          targetMatchDate={effectiveTargetMatchDate}
          availableTargetMatches={effectiveTargetMatches}
          targetMatchOpponent={effectiveTargetMatchOpponent}
          onSaveReferences={handleSavePlayerReferences}
          savingReferences={savingPlayerReferences}
        />
        {updatingTargetMatch ? (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-gray-950/25 backdrop-blur-[1px]">
            <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 shadow-lg ${
              isDarkMode ? "bg-gray-950 text-gray-100" : "bg-white text-gray-900"
            }`}>
              <Loader2 className="h-5 w-5 animate-spin text-cyan-500" />
              <div>
                <div className="text-sm font-semibold">
                  {t("weekly_recap.loading_match", { defaultValue: "Loading match data..." })}
                </div>
                <div className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  {t("weekly_recap.loading_match_hint", {
                    defaultValue: "Updating weekly loads for the selected match.",
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {toolbar}
      {selectedPlayer ? renderPlayerDetail() : renderPlayerGrid()}

      <WeeklyRecapPlanningModal
        isOpen={planningModalOpen}
        isDarkMode={isDarkMode}
        metrics={filterWeeklyRecapLoadMetrics(allMetrics)}
        externalMetrics={WEEKLY_RECAP_LOAD_METRICS}
        internalMetrics={WEEKLY_RECAP_INTERNAL_METRICS}
        jDays={selectedBundle?.jDays || effectiveJDays || defaultJDays}
        initialMatrix={globalPlanPercentages}
        initialDisplayConfig={displayConfig}
        initialMicrocycle={microcycleConfig}
        onClose={() => setPlanningModalOpen(false)}
        onSave={saveGlobalConfiguration}
        calculateSimilarityFromDay={calculateSimilarityFromDay}
      />
    </div>
  );
}
